import React, { useState } from 'react';
import { useMetrics } from '../contexts/MetricsContext';
import EditMetricModal from './EditMetricModal';
import LogMetricModal from './LogMetricModal';
import MetricDetail from './MetricDetail';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './MetricsSection.css';
import './MetricRow.css';

// Sortable version of MetricRow
const SortableMetricRow = ({ metric, onLog, onOpen }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: metric.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const handleLogClick = (e) => {
    e.stopPropagation();
    onLog(metric);
  };

  const handleRowClick = () => {
    onOpen(metric);
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`metric-row ${isDragging ? 'dragging' : ''}`}
      onClick={handleRowClick}
    >
      <div className="metric-row-content">
        <div 
          className="drag-handle"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </div>
        <span className="metric-name">{metric.name}</span>
        <div className="metric-actions">
          <button 
            className="log-btn"
            onClick={handleLogClick}
            title="Log entry for this metric"
          >
            Log
          </button>
        </div>
      </div>
    </div>
  );
};

const MetricsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [loggingMetric, setLoggingMetric] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [deletingMetric, setDeletingMetric] = useState(null);
  const { metrics, loading, error, addMetric, updateMetric, updateMetricOrder, deleteMetric, addLogEntry } = useMetrics();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = metrics.findIndex(metric => metric.id === active.id);
      const newIndex = metrics.findIndex(metric => metric.id === over.id);
      
      const newMetrics = arrayMove(metrics, oldIndex, newIndex);
      
      // Update the order field for all metrics
      const updates = newMetrics.map((metric, index) => ({
        ...metric,
        order: index + 1
      }));

      // Update all metrics in parallel
      try {
        await Promise.all(
          updates.map(metric => 
            updateMetricOrder(metric.id, metric.order)
          )
        );
      } catch (error) {
        console.error('Error updating metric order:', error);
        // Refresh metrics to get the correct order from the server
        window.location.reload();
      }
    }
  };

  const handleAddMetric = () => {
    setEditingMetric(null);
    setIsModalOpen(true);
  };

  const handleEditMetric = (metric) => {
    setEditingMetric(metric);
    setIsModalOpen(true);
  };

  const handleSaveMetric = async (metricData) => {
    try {
      if (editingMetric) {
        await updateMetric(editingMetric.id, metricData);
      } else {
        await addMetric(metricData);
      }
      setIsModalOpen(false);
      setEditingMetric(null);
    } catch (error) {
      console.error('Error saving metric:', error);
      // Error is handled by the modal
      throw error;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMetric(null);
  };

  const handleDeleteMetric = (metric) => {
    setDeletingMetric(metric);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingMetric) return;
    
    try {
      await deleteMetric(deletingMetric.id);
      setSelectedMetric(null); // Close detail view if deleting current metric
    } catch (error) {
      alert('Error deleting metric: ' + error.message);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingMetric(null);
  };

  const handleBackToList = () => {
    setSelectedMetric(null);
  };

  const handleLogMetric = (metric) => {
    setLoggingMetric(metric);
    setIsLogModalOpen(true);
  };

  const handleOpenMetric = (metric) => {
    setSelectedMetric(metric);
  };

  const handleSaveLogEntry = async (logEntry) => {
    try {
      await addLogEntry(logEntry);
      // Success is handled by the context, no need for alert
    } catch (error) {
      console.error('Error saving log entry:', error);
      // Error is handled by the modal
      throw error;
    }
  };

  const handleCloseLogModal = () => {
    setIsLogModalOpen(false);
    setLoggingMetric(null);
  };

  // If a metric is selected, show the detail view
  if (selectedMetric) {
    return (
      <div className="metrics-section">
        <MetricDetail
          metric={selectedMetric}
          onBack={handleBackToList}
          onEdit={handleEditMetric}
          onDelete={handleDeleteMetric}
          onLog={handleLogMetric}
        />
        
        <EditMetricModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveMetric}
          editingMetric={editingMetric}
        />
        
        <LogMetricModal
          isOpen={isLogModalOpen}
          onClose={handleCloseLogModal}
          onSave={handleSaveLogEntry}
          metric={loggingMetric}
        />
        
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          metricName={deletingMetric?.name}
        />
      </div>
    );
  }

  // Otherwise show the list view
  return (
    <div className="metrics-section">
      <div className="metrics-header">
        <h2>Metrics</h2>
      </div>
      
      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
        </div>
      )}

      <div className="metrics-list">
        {loading ? (
          <div className="loading-message">
            <p>Loading metrics...</p>
          </div>
        ) : metrics.length === 0 ? (
          <div className="no-metrics">
            <p>No metrics created yet. Click "Add New Metric" below to get started.</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={metrics.map(metric => metric.id)}
              strategy={verticalListSortingStrategy}
            >
              {metrics.map(metric => (
                <SortableMetricRow
                  key={metric.id}
                  metric={metric}
                  onLog={handleLogMetric}
                  onOpen={handleOpenMetric}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="add-metric-section">
        <button 
          className="add-metric-text-btn"
          onClick={handleAddMetric}
        >
          Add New Metric
        </button>
      </div>
      
      <EditMetricModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMetric}
        editingMetric={editingMetric}
      />
      
      <LogMetricModal
        isOpen={isLogModalOpen}
        onClose={handleCloseLogModal}
        onSave={handleSaveLogEntry}
        metric={loggingMetric}
      />
      
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        metricName={deletingMetric?.name}
      />
    </div>
  );
};

export default MetricsSection;
